import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api from "@/lib/api";
import { payWithRazorpayOnBehalf } from "@/lib/razorpay";

const OFFLINE_METHODS = ["Cash", "Cheque", "UPI", "Bank Transfer", "DD", "Other"];

/**
 * TakePaymentDialog — Two-in-one modal to record either an ONLINE payment
 * (Razorpay Checkout opens with the target member's prefill) or an OFFLINE
 * payment (Cash/Cheque/UPI/etc.).
 *
 * Props:
 *   open, onOpenChange
 *   member: { id, full_name, email, mobile }
 *   defaultMode: "offline" | "online" (default "offline")
 *   onSuccess(): callback to refresh the parent list
 */
export default function TakePaymentDialog({ open, onOpenChange, member, defaultMode = "offline", onSuccess }) {
  const [mode, setMode] = useState(defaultMode);
  const [method, setMethod] = useState("Cash");
  const [reference, setReference] = useState("");
  const [amount, setAmount] = useState(1300);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  React.useEffect(() => {
    // Reset when member changes
    if (open) {
      setMode(defaultMode);
      setMethod("Cash");
      setReference("");
      setAmount(1300);
      setDate(new Date().toISOString().slice(0, 10));
      setNote("");
    }
  }, [open, defaultMode, member?.id]);

  if (!member) return null;

  const submitOffline = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/members/${member.id}/mark-paid`, {
        method,
        reference: reference.trim(),
        amount: Number(amount) || 0,
        date: date ? new Date(date).toISOString() : undefined,
        note: note.trim(),
      });
      toast.success(`Payment recorded for ${member.full_name}`);
      onOpenChange(false);
      onSuccess?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not record payment");
    } finally {
      setBusy(false);
    }
  };

  const submitOnline = async () => {
    setBusy(true);
    try {
      await payWithRazorpayOnBehalf({
        memberId: member.id,
        amount: Number(amount) || 1300,
        purpose: "membership",
      });
      toast.success(`Payment collected from ${member.full_name}`);
      onOpenChange(false);
      onSuccess?.();
    } catch (e) {
      if (e?.message === "PAYMENT_CANCELLED") {
        toast.info("Payment cancelled");
      } else {
        toast.error(e?.response?.data?.detail || e?.message || "Payment failed");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="take-payment-dialog">
        <DialogHeader>
          <DialogTitle>Collect membership fee</DialogTitle>
          <DialogDescription>
            For <span className="font-semibold text-foreground">{member.full_name}</span>
            {member.email && <> · <span className="text-xs">{member.email}</span></>}
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-2 mb-2">
          <Button
            variant={mode === "offline" ? "default" : "outline"}
            onClick={() => setMode("offline")}
            className={mode === "offline" ? "bg-brand-blue hover:bg-brand-blue/90 text-white" : ""}
            data-testid="take-payment-mode-offline"
          >
            Record Offline
          </Button>
          <Button
            variant={mode === "online" ? "default" : "outline"}
            onClick={() => setMode("online")}
            className={mode === "online" ? "bg-brand-blue hover:bg-brand-blue/90 text-white" : ""}
            data-testid="take-payment-mode-online"
          >
            Take Online (Razorpay)
          </Button>
        </div>

        {mode === "offline" ? (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="tp-method">Method</Label>
                <Select value={method} onValueChange={setMethod}>
                  <SelectTrigger id="tp-method" data-testid="take-payment-method"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {OFFLINE_METHODS.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="tp-amount">Amount (₹)</Label>
                <Input id="tp-amount" type="number" min={1} value={amount} onChange={e => setAmount(e.target.value)} data-testid="take-payment-amount" />
              </div>
            </div>
            <div>
              <Label htmlFor="tp-ref">Reference # <span className="text-xs text-muted-foreground">(cheque no / UPI ref / receipt)</span></Label>
              <Input id="tp-ref" value={reference} onChange={e => setReference(e.target.value)} placeholder={method === "Cheque" ? "CHQ-104582" : method === "UPI" ? "UPI-TXN-ID" : "Optional"} data-testid="take-payment-reference" />
            </div>
            <div>
              <Label htmlFor="tp-date">Date</Label>
              <Input id="tp-date" type="date" value={date} onChange={e => setDate(e.target.value)} data-testid="take-payment-date" />
            </div>
            <div>
              <Label htmlFor="tp-note">Note <span className="text-xs text-muted-foreground">(optional, internal)</span></Label>
              <Input id="tp-note" value={note} onChange={e => setNote(e.target.value)} placeholder="e.g. Paid in district office" data-testid="take-payment-note" />
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <Label htmlFor="tp-amount-online">Amount (₹)</Label>
              <Input id="tp-amount-online" type="number" min={1} value={amount} onChange={e => setAmount(e.target.value)} data-testid="take-payment-amount" />
            </div>
            <p className="text-xs text-muted-foreground bg-muted/50 rounded-md p-2">
              Razorpay Checkout will open in this window with the member&apos;s details pre-filled. The member can pay by card / UPI / net-banking now, or you can hand them the device.
            </p>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          {mode === "offline" ? (
            <Button onClick={submitOffline} disabled={busy || !amount} className="bg-brand-blue hover:bg-brand-blue/90 text-white" data-testid="take-payment-submit-offline">
              {busy ? "Recording…" : "Record Payment"}
            </Button>
          ) : (
            <Button onClick={submitOnline} disabled={busy || !amount} className="bg-brand-blue hover:bg-brand-blue/90 text-white" data-testid="take-payment-submit-online">
              {busy ? "Opening…" : "Open Razorpay"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
